import { useRef } from 'react'

import { CrossFadeImage } from '@syconium/little-miss-figgy'

import { useTranslation } from '../../../../../app/_providers/TranslationProvider.client'
import { useEmbroideryGarment } from '../../../../../containers/EmbroideryContainer/providers/EmbroideryGarmentProvider'
import { useEmbroiderySelection } from '../../../../../containers/EmbroideryContainer/providers/EmbroiderySelectionProvider'
import { EmbroideryThreadFilter } from '../../primitives/EmbroideryThreadFilter'
import { EmbroideryPreviewPlacement } from '../../primitives/preview/EmbroideryPreviewPlacement'
import {
  PreviewPlacement,
  getPreviewOffset,
  groupMarksByPlacement,
  mergePreviewMarks,
} from '../../primitives/preview/previewMarks'

import { usePreviewDraft } from './PreviewDraftContext'
import { CloseUp, Frame, Layer } from './styles'

const IMAGE_ASPECT_RATIOS = { sm: 1, md: 1 } as const
const IMAGE_WIDTHS = { unit: 'px', xs: 320, sm: 360, md: 480, lg: 550, xl: 640 } as const

/** Centered and level. The marks size themselves for the close-up, so it adds no scale. */
const CLOSE_UP_OFFSET = { x: 0.5, y: 0.5, rotation: 0, scale: 1 } as const

/**
 * The garment with its saved and in-progress embroidery drawn in place. While an editor is focused
 * on a placement, it fades to a close-up showing only that placement's embroidery.
 */
export const EmbroideryPreview: React.FC = () => {
  const { productEmbroideryConfig, productColorHex } = useEmbroideryGarment()
  const { items } = useEmbroiderySelection()
  const draft = usePreviewDraft()
  const focusedPlacementId = draft?.focusedPlacementId
  const {
    embroidery: {
      modal: {
        carousel: {
          accessibility: { previewImage: imageAlt },
          textPlaceholder,
        },
      },
    },
  } = useTranslation()

  const flatlay = productEmbroideryConfig?.flatlay
  const placements = groupMarksByPlacement(mergePreviewMarks(items, draft))
  const isCloseUp = focusedPlacementId !== undefined

  // Keeps the focused placement as it was last drawn, so the close-up still shows it while it fades
  // out, even after the draft that drew it is gone.
  const closeUpPlacementRef = useRef<PreviewPlacement | undefined>(undefined)
  if (isCloseUp) {
    closeUpPlacementRef.current = placements.find(
      placement => placement.placementId === focusedPlacementId
    )
  }
  const closeUpPlacement = closeUpPlacementRef.current

  if (!flatlay && !isCloseUp && !closeUpPlacement) return null

  return (
    <>
      <CloseUp
        $isVisible={isCloseUp}
        style={{ backgroundColor: productColorHex }}
        data-testid='embroidery-preview-close-up'
      >
        {closeUpPlacement && (
          <EmbroideryPreviewPlacement
            placement={closeUpPlacement}
            offset={CLOSE_UP_OFFSET}
            productColorHex={productColorHex}
            textPlaceholder={textPlaceholder}
            showThreadEffect={false}
            centerOnText={false}
            isCloseUp
          />
        )}
      </CloseUp>
      <Frame>
        <EmbroideryThreadFilter />
        {flatlay && (
          <Layer
            $isVisible={!isCloseUp}
            aria-hidden={isCloseUp}
            data-testid='embroidery-preview-flatlay'
          >
            <CrossFadeImage
              alt={imageAlt}
              aspectRatios={IMAGE_ASPECT_RATIOS}
              src={flatlay.image.url}
              widths={IMAGE_WIDTHS}
              transparentBackground
            />
            {placements.map(placement => {
              const offset = getPreviewOffset(flatlay, placement.placementId)
              if (!offset) return null
              return (
                <EmbroideryPreviewPlacement
                  key={placement.placementId}
                  placement={placement}
                  offset={offset}
                  productColorHex={productColorHex}
                  textPlaceholder={textPlaceholder}
                />
              )
            })}
          </Layer>
        )}
      </Frame>
    </>
  )
}
